import axios, { AxiosInstance } from 'axios';
import { getVoiceFallbackClient } from './_shared';

// ── Per-campaign compliance override (/api/campaigns/{id}/compliance) ─
// Verified 2026-08-19: the PATCH request body (CampaignComplianceOverrideRequest)
// is documented — `calling_hours`/`frequency_caps`/`recording_disclosure` are
// excluded from the write side below: all three are `additionalProperties: true`
// objects with no documented sub-shape, same reasoning as `guardrails` in F11.
// fetchCampaignCompliance's GET response is now declared server-side as
// `CampaignComplianceResponse` (confirmed live 2026-08-21): every field is a
// `{ value, source }` pair.

export type CampaignComplianceOverridePatch = {
  quiet_periods?: string[];
  consent_evidence_retention_years?: number;
  ai_disclosure_template?: string;
  state_exclusions_add?: string[];
};

/** The only three values the real (campaign-scoped) endpoint ever sends —
 * confirmed live 2026-08-21: `"platform"` (hardcoded, never configurable),
 * `"workspace_snapshot"` (frozen onto the campaign at creation from the
 * workspace's defaults), `"campaign_override"` (set via `PATCH` on this same
 * endpoint, tighten-only — loosening 422s). */
export type ComplianceFieldSourceTag = 'platform' | 'workspace_snapshot' | 'campaign_override';

/** One field of `CampaignComplianceResponse` — confirmed live 2026-08-21.
 * `source` is typed as plain `string` (not `ComplianceFieldSourceTag`) since
 * callers may synthesize this same shape locally for views with no real
 * source tag (e.g. `CampaignComplianceEffectiveConfig`'s workspace-defaults
 * fallback). */
export type ComplianceFieldValue<T = unknown> = {
  value: T;
  source: string;
};

/** `GET /api/campaigns/{campaign_id}/compliance` response — confirmed live
 * 2026-08-21 (previously undeclared `{}` in openapi.json). */
export type CampaignComplianceResponse = {
  calling_hours: ComplianceFieldValue;
  frequency_caps: ComplianceFieldValue;
  quiet_periods: ComplianceFieldValue<string[]>;
  consent_evidence_retention_years: ComplianceFieldValue<number>;
  ai_disclosure_template: ComplianceFieldValue<string>;
  recording_disclosure: ComplianceFieldValue;
  state_exclusions: ComplianceFieldValue<string[]>;
  dnc_optout_scrub: ComplianceFieldValue;
  ai_disclosure_postprocess: ComplianceFieldValue;
  revoke_all_ready: ComplianceFieldValue;
};

/** `GET /api/campaigns/{campaign_id}/compliance` — this campaign's effective
 * compliance config (workspace defaults + any campaign-level tightening). */
export async function fetchCampaignCompliance(
  campaignId: string,
  axiosInstance?: AxiosInstance
): Promise<CampaignComplianceResponse> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.get<CampaignComplianceResponse>(`/api/campaigns/${campaignId}/compliance`);
  return response.data;
}

/** `PATCH /api/campaigns/{campaign_id}/compliance` — tighten-only vs. the
 * campaign's frozen workspace snapshot; 422 `LOOSENS_WORKSPACE_DEFAULT` on any
 * field that would loosen it. */
export async function updateCampaignCompliance(
  campaignId: string,
  patch: CampaignComplianceOverridePatch,
  axiosInstance?: AxiosInstance
): Promise<CampaignComplianceResponse> {
  const client = axiosInstance || getVoiceFallbackClient();
  const response = await client.patch<CampaignComplianceResponse>(`/api/campaigns/${campaignId}/compliance`, patch);
  return response.data;
}
